import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Type definitions

/** A note as setNoteReminder takes it (a NoteRead, or a tile's copy). */
export interface ReminderNote {
  id?: number;
  title?: string;
  body?: string | null;
  text?: string;
}

/**
 * A note reminder. They are this device's: kept in this slice and in
 * localStorage ("note_reminders"), never sent to the API.
 */
export interface Reminder {
  id: number;
  /** The note's id: what the Notes page finds a note's reminder by. */
  noteId?: number;
  /** The note as the reminder shows it. */
  note?: { id?: number; title?: string; text?: string };
  /** When it is due, in ms since the epoch. */
  due?: number;
  /** Done: true, or when (ms since the epoch). */
  completed?: boolean | number;
  created?: number;
  // Older stored copies' fields.
  title?: string;
  description?: string;
  dueDate?: string;
  priority?: "low" | "medium" | "high";
  workspaceId?: number;
  time?: number;
  text?: string | ReminderNote;
}

/** A note's open (not completed) reminder. */
export function findNoteReminder(
  reminders: readonly Reminder[],
  noteId: number | undefined,
): Reminder | undefined {
  if (noteId === undefined) return undefined;
  return reminders.find((r) => r.noteId === noteId && !r.completed);
}

export type ReminderPopup =
  | number
  | null
  | {
      id: number;
      message?: string;
      type?: "info" | "warning" | "error" | "success";
      duration?: number;
    };

export interface ReminderState {
  reminders: Reminder[];
  showReminderDrawer: boolean;
  reminderPopup: ReminderPopup | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReminderState = {
  reminders: [],
  showReminderDrawer: false,
  reminderPopup: null,
  loading: false,
  error: null,
};

const reminderSlice = createSlice({
  name: "reminder",
  initialState,
  reducers: {
    setReminders: (state, action: PayloadAction<Reminder[]>) => {
      state.reminders = action.payload;
    },
    addReminder: (state, action: PayloadAction<Reminder>) => {
      state.reminders.push(action.payload);
    },
    updateReminder: (state, action: PayloadAction<Reminder>) => {
      const index = state.reminders.findIndex(
        (r) => r.id === action.payload.id,
      );
      if (index !== -1) {
        state.reminders[index] = action.payload;
      }
    },
    removeReminder: (state, action: PayloadAction<number>) => {
      state.reminders = state.reminders.filter((r) => r.id !== action.payload);
    },
    setShowReminderDrawer: (state, action: PayloadAction<boolean>) => {
      state.showReminderDrawer = action.payload;
    },
    setReminderPopup: (state, action: PayloadAction<ReminderPopup | null>) => {
      state.reminderPopup = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setReminders,
  addReminder,
  updateReminder,
  removeReminder,
  setShowReminderDrawer,
  setReminderPopup,
  setLoading,
  setError,
} = reminderSlice.actions;

export default reminderSlice.reducer;
